import { useCallback, useState, useEffect, FC, useRef } from 'react';
import * as L from './InstrumentsMainPageStyles';
import { SpinnerSize, Spinner, Separator, SearchBox, DefaultButton } from '@fluentui/react';
import FundService, { IFund } from '../../services/FundService';
import Filters from './Filters';
import FeaturedManagers from '../FeaturedManagers/FeaturedManagers';
import Auth from '../../Auth/Auth';
import groupFundsByFamily from '../../utils/groupFundsByFamily';
import { Link } from "react-router-dom";
import { ai } from '../../services/TelemetryService';
import { useHistory } from "react-router-dom";
import { translate } from "../../utils/i18n";
import { themes } from '../ThemeContext';
import React from 'react';
import SearchFundsPrincipalTable, { Fund } from '../tables/buscadorFondosTable/SearchFundsPrincipalTable';

const FILTERS_STORAGE_KEY = 'searchFundsFilters';

interface IPersistedFilters
{
    available?: boolean;
    income?: boolean;
    switchable?: boolean;
    indexed?: boolean;
    classCategory?: boolean;
}

const loadFiltersFromStorage = (): Partial<IPersistedFilters> =>
{
    try
    {
        const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
        
        if(stored)
        {
            const parsed = JSON.parse(stored);
            
            // Convertimos null de vuelta a undefined para mantener coherencia
            const result: Partial<IPersistedFilters> = {};
            
            if(parsed.available !== null && parsed.available !== undefined)
            {
                result.available = parsed.available;
            }
            else if(parsed.hasOwnProperty('available'))
            {
                result.available = undefined;
            }
            
            if(parsed.income !== null && parsed.income !== undefined)
            {
                result.income = parsed.income;
            }
            else if(parsed.hasOwnProperty('income'))
            {
                result.income = undefined;
            }
            
            if(parsed.switchable !== null && parsed.switchable !== undefined)
            {
                result.switchable = parsed.switchable;
            }
            else if(parsed.hasOwnProperty('switchable'))
            {
                result.switchable = undefined;
            }
            
            if(parsed.indexed !== null && parsed.indexed !== undefined)
            {
                result.indexed = parsed.indexed;
            }
            else if(parsed.hasOwnProperty('indexed'))
            {
                result.indexed = undefined;
            }
            
            if(parsed.classCategory !== null && parsed.classCategory !== undefined)
            {
                result.classCategory = parsed.classCategory;
            }
            else if(parsed.hasOwnProperty('classCategory'))
            {
                result.classCategory = undefined;
            }
            
            console.log('Filtros cargados desde localStorage:', result);
            return result;
        }
    }
    catch(error)
    {
        console.warn('Error loading filters from localStorage:', error);
    }
    console.log('No hay filtros guardados, usando valores por defecto');
    return {};
};

const saveFiltersToStorage = (filters: IPersistedFilters): void =>
{
    try
    {
        // Convertimos undefined a null para que JSON.stringify los preserve
        const filtersToStore =
        {
            available: filters.available === undefined ? null : filters.available,
            income: filters.income === undefined ? null : filters.income,
            switchable: filters.switchable === undefined ? null : filters.switchable,
            indexed: filters.indexed === undefined ? null : filters.indexed,
            classCategory: filters.classCategory === undefined ? null : filters.classCategory
        };
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToStore));
        console.log('Filtros guardados en localStorage:', filtersToStore);
    }
    catch(error)
    {
        console.warn('Error saving filters to localStorage:', error);
    }
};

interface ISubcategory
{
    morningStarCategoryId: string;
    name: string;
}

interface IFilters
{
    subcategory: ISubcategory[];
    manager: string[];
    currency: string[];
    available?: boolean;
    income?: boolean;
    switchable?: boolean;
    indexed?: boolean;
    class?: boolean;
    classCategory?: boolean;
}

interface IFundGroup
{
    family: string;
    children: IFund[];
}

interface IFundResponse
{
    results: IFund[];
    total: number;
    total_Family?: number;
}

interface ILocationState
{
    searchTerm?: string;
    subcategory?: ISubcategory[];
    manager?: string[];
    currency?: string[];
    available?: boolean;
    income?: boolean;
    switchable?: boolean;
    indexed?: boolean;
    class?: boolean;
    classCategory?: boolean;
}

interface ILocation
{
    state?: ILocationState;
}

interface IInstrumentsProps
{
    defaultFilters?: Partial<IFilters>;
    location?: ILocation;
    publicVersion?: boolean;
    fromEF?: boolean;
}

let lastPromise: Promise<IFundResponse> | null = null;

const InstrumentsMainPage: FC<IInstrumentsProps> = ({defaultFilters = {}, location, publicVersion, fromEF = false}) =>
{
    const localTheme = window.localStorage.getItem("theme") || "light";
    const theme = {palette: localTheme === "dark" ? themes.dark : themes.light};
    
    // Usar lazy initialization para que solo se ejecute una vez al montar el componente
    const [filters, setFilters] = useState<IFilters>(() =>
    {
        console.log('Inicializando filtros - SOLO UNA VEZ');
        
        const savedFilters = loadFiltersFromStorage();
        console.log('Filtros cargados desde localStorage:', savedFilters);
        
        const defaultFilterValues: IFilters =
        {
            subcategory: [],
            manager: [],
            currency: [],
            available: true,          // SÍ por defecto
            income: undefined,        // SIN MARCAR por defecto
            switchable: undefined,    // SIN MARCAR por defecto
            indexed: undefined,       // SIN MARCAR por defecto
            class: undefined,
            classCategory: true,      // SÍ por defecto
            ...defaultFilters,
            ...location?.state
        };

        // Verificamos si hay filtros guardados (incluyendo propiedades con valor undefined)
        const hasStoredFilters = savedFilters &&
        (
            savedFilters.hasOwnProperty('available') ||
            savedFilters.hasOwnProperty('income') ||
            savedFilters.hasOwnProperty('switchable') ||
            savedFilters.hasOwnProperty('indexed') ||
            savedFilters.hasOwnProperty('classCategory')
        );

        console.log('¿Hay filtros guardados?', hasStoredFilters);

        // Si hay filtros guardados, aplicamos solo a los de ThreeWayToggle
        if(hasStoredFilters)
        {
            const initialFilters =
            {
                ...defaultFilterValues,
                available: savedFilters.hasOwnProperty('available') ? savedFilters.available : defaultFilterValues.available,
                income: savedFilters.hasOwnProperty('income') ? savedFilters.income : defaultFilterValues.income,
                switchable: savedFilters.hasOwnProperty('switchable') ? savedFilters.switchable : defaultFilterValues.switchable,
                indexed: savedFilters.hasOwnProperty('indexed') ? savedFilters.indexed : defaultFilterValues.indexed,
                classCategory: savedFilters.hasOwnProperty('classCategory') ? savedFilters.classCategory : defaultFilterValues.classCategory,
            };
            console.log('Usando filtros guardados:', initialFilters);
            return initialFilters;
        }

        console.log('Usando filtros por defecto:', defaultFilterValues);
        return defaultFilterValues;
    });
    
    const isInitialMount = useRef(true);
    
    let history = useHistory();
    //Para las redirecciones con término de búsqueda incluidas
    const searchTerm = (history?.location?.state as ILocationState)?.searchTerm ?? "";
    const [updateSearch, setUpdateSearch] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [recordsTotal, setRecordsTotal] = useState<number>(0);
    const [familyRecordsTotal, setFamilyRecordsTotal] = useState<number>(0);

    const [tableSearchTerm, setTableSearchTerm] = useState<string>(searchTerm);
    const [loading, setLoading] = useState<boolean>(false);
    const [fundGroups, setFundGroups] = useState<IFundGroup[]>([]);
    const [productsPerPage, setProductsPerPage] = useState<number>(Number(Auth.getQtyPage()) || 10);
    const [isManagerQueryPresent, setIsManagerQueryPresent] = useState<boolean>(false);
    const appInsights = ai.reactPlugin;

    // Guardar filtros en localStorage cuando cambien (solo los ThreeWayToggle)
    // No guardamos en la primera carga para evitar sobrescribir los filtros guardados
    useEffect(() =>
    {
        if(!isInitialMount.current)
        {
            const filtersToSave: IPersistedFilters =
            {
                available: filters.available,
                income: filters.income,
                switchable: filters.switchable,
                indexed: filters.indexed,
                classCategory: filters.classCategory
            };
            console.log('Guardando filtros en localStorage:', filtersToSave);
            saveFiltersToStorage(filtersToSave);
        }
        else
        {
            console.log('Saltando guardado inicial para evitar sobrescribir filtros guardados');
            isInitialMount.current = false;
        }
    }, [filters.available, filters.income, filters.switchable, filters.indexed, filters.classCategory]);

    useEffect(() =>
    {
        const params = new Proxy(new URLSearchParams(window.location.search),
        {
            get: (searchParams, prop: string) => searchParams.get(prop),
        });

        if((params as any).manager !== null)
        {
            handleClick((params as any).manager);
            setIsManagerQueryPresent(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() =>
    {
        setTableSearchTerm((history?.location?.state as ILocationState)?.searchTerm ?? "");
        setUpdateSearch(c => !c);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, history?.location?.state]);

    const searchFunds = (): void =>
    {
        let {subcategory, manager, currency, available, income, switchable, indexed, classCategory} = filters;
        let subcategories = subcategory.map((sc) =>
        {
            return sc.morningStarCategoryId;
        });

        let promise = lastPromise = FundService.getFunds(page, productsPerPage, tableSearchTerm.toUpperCase(), subcategories.length > 0 ? subcategories.join(',') : undefined, available, income, switchable, indexed, manager.length > 0 ? manager : undefined, currency.length > 0 ? currency : undefined, classCategory);

        promise.then((response: IFundResponse) =>
        {
            if(promise !== lastPromise)
            {
                return;
            }
            setFamilyRecordsTotal(response?.total_Family ?? 0);
            setRecordsTotal(response.total);
            setFundGroups(groupFundsByFamily(response.results));
            setLoading(false);
        })
        .catch((error: any) =>
        {
            if(promise !== lastPromise)
            {
                return;
            }
        })
        .finally(() =>
        {
            if(promise !== lastPromise)
            {
                return;
            }
        });
    };

    const handleKey = (e: React.KeyboardEvent<HTMLInputElement>): void =>
    {
        if(e.key === 'Enter')
        {
            load();
        }
    };

    const load = (): void =>
    {
        setFamilyRecordsTotal(0);
        setRecordsTotal(0);
        setLoading(true);
        setFundGroups([]);
        setTimeout(() => searchFunds());
    };

    useEffect(() =>
    {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, productsPerPage, updateSearch]);

    // Búsqueda automática cuando cambien los filtros
    useEffect(() =>
    {
        if(!isInitialMount.current)
        {
            const timeoutId = setTimeout(() =>
            {
                console.log('Búsqueda automática activada por cambio de filtros');
                setPage(0); // Reseteamos a la primera página
                load();
            }, 300); // Debounce de 300ms

            return () => clearTimeout(timeoutId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.subcategory, filters.manager, filters.currency, filters.available, filters.income, filters.switchable, filters.indexed, filters.classCategory]);

    const handleChange = useCallback((value: any, filterId: keyof IFilters): void =>
    {
        setFilters(currentFilters => ({...currentFilters, [filterId]: value}));
    }, []);

    const handleClick = (managerNames: string | string[]): boolean | void =>
    {
        if(publicVersion && isManagerQueryPresent)
        {
            return false;
        }

        const managerNamesArray = Array.isArray(managerNames) ? managerNames : [managerNames];
        const alreadySelected = filters.manager.some((name) => managerNamesArray.includes(name));
        const filteredManagers = filters.manager.filter((name) => !managerNamesArray.includes(name));

        if(alreadySelected)
        {
            setFilters(
            {
                ...filters,
                manager: [...filteredManagers],
            });
        }
        else
        {
            appInsights.trackEvent({name: "QuickLink", properties: { managerName: translate(managerNamesArray[0])}});

            setFilters(
            {
                ...filters,
                manager: [...filteredManagers, ...managerNamesArray],
            });
        }
        setUpdateSearch(c => !c);
    };

    return(
        <L.Wrapper>
            <L.HeaderContainer>
                <L.TitleContainer>
                    <L.Title>Buscador de fondos</L.Title>
                </L.TitleContainer>
                {publicVersion && !isManagerQueryPresent &&
                (
                    <L.ButtonContainer>
                        <Link to="/">
                            <L.ButtonRegister>
                                Regístrate para operar
                            </L.ButtonRegister>
                        </Link>
                    </L.ButtonContainer>
                )}
            </L.HeaderContainer>
            <Separator />
            <L.FMContainer>
                <L.FMTitleContainer>
                    <L.FMTitle>GESTORAS DESTACADAS</L.FMTitle>
                </L.FMTitleContainer>
                <L.FMCardsContainer>
                    <FeaturedManagers
                        selectedManagers={filters.manager}
                        onClick={handleClick}
                        readOnly={publicVersion && isManagerQueryPresent}
                    />
                </L.FMCardsContainer>
            </L.FMContainer>
            <L.FiltersWrapper>
                <Filters onChange={handleChange} selectedFilters={filters} hiddenFilters={publicVersion && isManagerQueryPresent ? {manager: true} : {}} />
            </L.FiltersWrapper>
            <L.TableWrapper theme={theme}>
                <L.TableWidthManager theme={theme}>
                    <L.TableFilterContainer>
                        <L.TRCountContainer>
                            <L.TRTitle theme={theme}>
                                Resultados de la búsqueda
                                <L.TRCount theme={theme}>{`(${familyRecordsTotal})`}</L.TRCount>
                            </L.TRTitle>
                        </L.TRCountContainer>
                        <L.InputSearchContainer theme={theme}>
                            <SearchBox onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => handleKey(e)}
                                placeholder="Buscar fondos..."
                                value={tableSearchTerm}
                                autoComplete="off"
                                onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) =>
                                {
                                    let text = newValue || event?.target.value || "";
                                    setTableSearchTerm(text);
                                }}
                            />
                        </L.InputSearchContainer>
                        <L.EditColumnContainer>
                            <DefaultButton text="Buscar" onClick={load} />
                        </L.EditColumnContainer>
                    </L.TableFilterContainer>
                    {loading ?
                    (
                        <Spinner size={SpinnerSize.large} />
                    ) :
                    (
                        <React.Fragment>
                            <L.TableContainer theme={theme}>
                                <SearchFundsPrincipalTable theme={theme}
                                    publicVersion={publicVersion || false}
                                    groups={fundGroups as Fund[]}
                                    columns={["Nombre", "Gestora", "Categoría", "Puntos IronIA"]}
                                    history={history}
                                    fromEF={fromEF}
                                />
                            </L.TableContainer>
                            <L.PaginationContainer theme={theme}>
                                <L.ProductsPerPage theme={theme}>
                                    <span>Productos por página</span>
                                    <select onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                                        {
                                            Auth.setQtyPage(parseInt(event.target.value));
                                            setProductsPerPage(parseInt(event.target.value));
                                        }}
                                        defaultValue={Number(Auth.getQtyPage()) || 10}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={30}>30</option>
                                        <option value={40}>40</option>
                                        <option value={50}>50</option>
                                    </select>
                                </L.ProductsPerPage>
                                <L.PageCount theme={theme}>
                                    <span>{`${page + 1}-${productsPerPage} de ${Math.ceil(recordsTotal / productsPerPage)}`}</span>
                                </L.PageCount>
                                <L.PageButtons theme={theme}>
                                    <button onClick={() =>
                                        {
                                            if(page > 0)
                                            {
                                                setPage(page - 1);
                                            }
                                        }}
                                        className={page === 0 ? "disabled" : ""}
                                    >
                                        <i className={"ironia-icon keyboard-arrow-left"} />
                                    </button>
                                    <button onClick={() =>
                                        {
                                            if(page < Math.ceil(recordsTotal / productsPerPage) - 1)
                                            {
                                                setPage(page + 1);
                                            }
                                        }}
                                        className={Math.ceil(recordsTotal / productsPerPage) === page + 1 ? "disabled" : ""}
                                    >
                                        <i className={"ironia-icon keyboard-arrow-right"} />
                                    </button>
                                </L.PageButtons>
                            </L.PaginationContainer>
                        </React.Fragment>
                    )}
                </L.TableWidthManager>
            </L.TableWrapper>
        </L.Wrapper>
    );
};

export default InstrumentsMainPage;